/**
 * Presentation-only multi-currency layer.
 * Never writes converted amounts to cart, checkout, or Shopify price fields.
 */
(function () {
  'use strict';

  var CACHE_PREFIX = 'native-currency:rates:';
  var DECIMAL_PLACES = { KWD: 3, BHD: 3, OMR: 3, JOD: 3 };
  var DEFAULT_DECIMALS = 2;
  var USD_PEGS = {
    AED: 3.6725,
    SAR: 3.75,
    QAR: 3.64,
    BHD: 0.376,
    OMR: 0.3845
  };
  var LABELS = {
    ar: {
      KWD: 'د.ك.',
      BHD: 'د.ب.',
      OMR: 'ر.ع.',
      SAR: '__SAR__',
      AED: 'د.إ.',
      QAR: 'ر.ق.',
      EGP: 'ج.م.',
      JOD: 'د.أ.',
      IQD: 'د.ع.',
      GBP: '£'
    },
    en: {
      KWD: 'K.D.',
      BHD: 'BHD',
      OMR: 'OMR',
      SAR: '__SAR__',
      AED: 'AED',
      QAR: 'QR',
      EGP: 'EGP',
      JOD: 'JOD',
      IQD: 'IQD',
      GBP: 'GBP'
    }
  };
  var SCOPED_CONTAINERS = [
    '.predictive-search',
    '#quick-buy-content',
    '[class*="usd-bundle"]',
    '.EComposer-theme',
    'product-list',
    'scroll-carousel',
    'product-recommendations',
    'recently-viewed-products',
    'cross-sell-recs',
    '.product-cross-sell',
    'essential-upsell-cross-sell-app-block',
    '[data-essential-upsell-element="container"]'
  ];

  var config = null;
  var ratesState = { rates: null, source: 'none', cacheHit: false };
  var updateTimer = 0;
  var started = false;
  var painting = false;
  var MAX_MAJOR = 99999;

  window.NATIVE_CURRENCY_DEBUG = window.NATIVE_CURRENCY_DEBUG === true;

  function parseConfig() {
    var node = document.getElementById('native-currency-config');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function debugEnabled() {
    if (window.NATIVE_CURRENCY_DEBUG) return true;
    if (config && config.debug) return true;
    try {
      return new URLSearchParams(window.location.search).get('native_currency_debug') === '1';
    } catch (error) {
      return false;
    }
  }

  function log() {
    if (!debugEnabled()) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[native-currency]');
    try {
      console.log.apply(console, args);
    } catch (error) {
      /* ignore */
    }
  }

  function decimalsFor(code) {
    return DECIMAL_PLACES[code] || DEFAULT_DECIMALS;
  }

  function subunitFor(code) {
    return Math.pow(10, decimalsFor(code));
  }

  function shopifySubunit(code) {
    if (!code) return 100;
    if (config && config.shopCurrency === code && Number(config.shopSubunit) > 0) {
      return Number(config.shopSubunit);
    }
    if (config && config.presentmentCurrency === code && Number(config.presentmentSubunit) > 0) {
      return Number(config.presentmentSubunit);
    }
    return 100;
  }

  function outputDecimals(code) {
    var inputSub = shopifySubunit(code);
    if (inputSub === 1000) return 3;
    if (inputSub === 1) return 0;
    return 2;
  }

  function localeCode() {
    var locale = (config && config.locale) || document.documentElement.lang || 'en';
    return String(locale).split('-')[0] || 'en';
  }

  function isArabic() {
    return localeCode() === 'ar';
  }

  function currencyLabel(code) {
    var pack = isArabic() ? LABELS.ar : LABELS.en;
    return pack[code] || code;
  }

  function targetCurrency() {
    var country = config && config.country;
    var map = (config && config.marketCurrencyMap) || {};
    return (country && map[country]) || (config && config.presentmentCurrency) || (config && config.shopCurrency);
  }

  function shopifyPresentmentRate() {
    var rate = window.Shopify && Shopify.currency && parseFloat(Shopify.currency.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  function presentmentToShopCents(presentmentCents, presentmentCurrency, shopCurrency) {
    var cents = Number(presentmentCents);
    if (!Number.isFinite(cents)) return null;
    if (!presentmentCurrency || !shopCurrency || presentmentCurrency === shopCurrency) {
      return Math.round(cents);
    }
    var rate = shopifyPresentmentRate();
    var presentmentMajor = cents / shopifySubunit(presentmentCurrency);
    var shopMajor = presentmentMajor / rate;
    if (!Number.isFinite(shopMajor)) return null;
    return Math.round(shopMajor * shopifySubunit(shopCurrency));
  }

  function staticRate(base, target) {
    if (base === target) return 1;
    if (USD_PEGS[base] && USD_PEGS[target]) {
      return USD_PEGS[target] / USD_PEGS[base];
    }
    return null;
  }

  function readCache(base) {
    try {
      var raw = localStorage.getItem(CACHE_PREFIX + base);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.rates || !parsed.expiresAt) return null;
      if (Date.now() > Number(parsed.expiresAt)) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  function writeCache(base, rates, source) {
    var hours = (config && Number(config.cacheHours)) || 12;
    var payload = {
      base: base,
      rates: rates,
      source: source,
      fetchedAt: Date.now(),
      expiresAt: Date.now() + hours * 60 * 60 * 1000
    };
    try {
      localStorage.setItem(CACHE_PREFIX + base, JSON.stringify(payload));
    } catch (error) {
      /* private mode */
    }
    return payload;
  }

  function fetchJson(url, timeoutMs) {
    var controller = new AbortController();
    var timer = setTimeout(function () {
      controller.abort();
    }, timeoutMs);
    return fetch(url, { signal: controller.signal, credentials: 'omit', cache: 'no-store' })
      .then(function (response) {
        if (!response.ok) throw new Error('http ' + response.status);
        return response.json();
      })
      .finally(function () {
        clearTimeout(timer);
      });
  }

  function parseOpenErApi(json) {
    if (!json || json.result !== 'success' || !json.rates) return null;
    return json.rates;
  }

  function parseFawaz(json, base) {
    if (!json) return null;
    var bucket = json[String(base).toLowerCase()];
    if (!bucket || typeof bucket !== 'object') return null;
    var rates = {};
    Object.keys(bucket).forEach(function (key) {
      rates[key.toUpperCase()] = bucket[key];
    });
    return rates;
  }

  var CurrencyService = {
    getRate: function (rates, base, target) {
      if (!base || !target) return null;
      if (base === target) return 1;
      if (rates && Number.isFinite(Number(rates[target])) && Number(rates[target]) > 0) {
        return Number(rates[target]);
      }
      return staticRate(base, target);
    },
    convert: function (amountMajor, base, target, rates) {
      var rate = this.getRate(rates, base, target);
      if (rate == null || !Number.isFinite(Number(amountMajor))) return null;
      return Number(amountMajor) * rate;
    },
    getRates: function (base) {
      var cached = readCache(base);
      if (cached) {
        ratesState = { rates: cached.rates, source: cached.source || 'cache', cacheHit: true };
        log('cache hit', { base: base, source: cached.source, expiresAt: cached.expiresAt });
        return Promise.resolve(cached.rates);
      }

      var timeoutMs = (config && Number(config.apiTimeoutMs)) || 2500;
      var primaryUrl = 'https://open.er-api.com/v6/latest/' + encodeURIComponent(base);
      var fallbackUrl =
        'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/' +
        encodeURIComponent(String(base).toLowerCase()) +
        '.json';

      return fetchJson(primaryUrl, timeoutMs)
        .then(function (json) {
          var rates = parseOpenErApi(json);
          if (!rates) throw new Error('primary-parse');
          writeCache(base, rates, 'open.er-api.com');
          ratesState = { rates: rates, source: 'open.er-api.com', cacheHit: false };
          log('api success', { provider: 'open.er-api.com', base: base });
          return rates;
        })
        .catch(function (primaryError) {
          log('primary api failed', primaryError && primaryError.message);
          return fetchJson(fallbackUrl, timeoutMs).then(function (json) {
            var rates = parseFawaz(json, base);
            if (!rates) throw new Error('fallback-parse');
            writeCache(base, rates, 'fawazahmed0');
            ratesState = { rates: rates, source: 'fawazahmed0', cacheHit: false };
            log('api success', { provider: 'fawazahmed0', base: base });
            return rates;
          });
        })
        .catch(function (fallbackError) {
          log('fallback api failed', fallbackError && fallbackError.message);
          ratesState = { rates: null, source: 'failed', cacheHit: false };
          return null;
        });
    }
  };

  function formatNumber(cents, currency) {
    var decimals = outputDecimals(currency);
    var major = Number(cents) / Math.pow(10, decimals);
    if (!Number.isFinite(major)) return null;
    try {
      return new Intl.NumberFormat(isArabic() ? 'ar' : 'en', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(major);
    } catch (error) {
      return major.toFixed(decimals);
    }
  }

  function convertPresentmentCents(presentmentCents, presentmentCurrency) {
    var shopCurrency = config.shopCurrency;
    var target = targetCurrency();
    var shopCents = presentmentToShopCents(presentmentCents, presentmentCurrency, shopCurrency);
    if (shopCents == null) return null;
    var shopMajor = shopCents / shopifySubunit(shopCurrency);
    var displayMajor = CurrencyService.convert(shopMajor, shopCurrency, target, ratesState.rates);
    if (displayMajor == null || !Number.isFinite(displayMajor)) return null;
    var displayCents = Math.round(displayMajor * Math.pow(10, outputDecimals(target)));
    if (!Number.isFinite(displayCents) || displayCents < 0 || displayCents > 1e10) return null;
    if (!Number.isFinite(displayMajor) || displayMajor > MAX_MAJOR) return null;
    return {
      shopCents: shopCents,
      displayCents: displayCents,
      target: target,
      rate: CurrencyService.getRate(ratesState.rates, shopCurrency, target)
    };
  }

  function findCurrencyLabel(el) {
    var parent = el.parentElement;
    if (!parent) return null;
    if (el.classList.contains('compare-digits')) {
      return parent.querySelector('.compare-currency');
    }
    return parent.querySelector('.price-currency') || parent.querySelector('.compare-currency');
  }

  function applyCurrencyLabel(el, label) {
    if (!el) return;
    if (isSarLabel(label)) {
      el.textContent = '';
      el.classList.add('f-price__currency', 'f-price__currency--sar');
      el.setAttribute('aria-label', 'SAR');
      return;
    }
    el.classList.remove('f-price__currency', 'f-price__currency--sar', 'sar-symbol');
    el.removeAttribute('aria-label');
    el.textContent = label;
  }

  function sarMarkHtml() {
    return '<span class="f-price__currency f-price__currency--sar" aria-label="SAR"></span>';
  }

  function labeledHtml(number, label) {
    if (isSarLabel(label)) {
      return number + ' ' + sarMarkHtml();
    }
    return number + ' ' + label;
  }

  function isSarLabel(label) {
    return label === '__SAR__' || label === '\uE900' || label === '\u20C1';
  }

  function paintNode(el, number, label) {
    if (el.tagName === 'OPTION') {
      var title = el.getAttribute('data-variant-title') || el.textContent.split('—')[0].trim();
      el.textContent = title + ' — ' + number + ' ' + (isSarLabel(label) ? 'SAR' : label);
      return;
    }

    if (el.hasAttribute('data-deema-amount')) {
      var period = el.getAttribute('data-deema-period') || '';
      if (isSarLabel(label)) {
        el.innerHTML = (number + ' ' + sarMarkHtml() + (period ? ' ' + period : '')).replace(/\s+/g, ' ').trim();
      } else {
        el.textContent = (number + ' ' + label + (period ? ' ' + period : '')).replace(/\s+/g, ' ').trim();
      }
      return;
    }

    if (el.classList.contains('price-digits') || el.classList.contains('compare-digits')) {
      el.textContent = number;
      var currencyEl = findCurrencyLabel(el);
      if (currencyEl) applyCurrencyLabel(currencyEl, label);
      return;
    }

    var digits = el.querySelector('.price-digits, .compare-digits');
    if (digits) {
      digits.textContent = number;
      var nestedLabel = el.querySelector('.price-currency, .compare-currency');
      if (nestedLabel) applyCurrencyLabel(nestedLabel, label);
      return;
    }

    if (isSarLabel(label)) {
      el.innerHTML = labeledHtml(number, label);
    } else {
      el.textContent = number + ' ' + label;
    }
  }

  function updateAll() {
    if (!config || painting) return;
    painting = true;
    try {
      var target = targetCurrency();
      var shopCurrency = config.shopCurrency;
      var nodes = document.querySelectorAll('[data-currency-price]');
      var converted = 0;

      nodes.forEach(function (el) {
        var raw = el.getAttribute('data-price');
        if (raw == null || raw === '') return;
        var presentmentCents = Number(raw);
        if (!Number.isFinite(presentmentCents)) return;

        var presentmentCurrency = el.getAttribute('data-presentment-currency') || config.presentmentCurrency;
        if (el.closest('.ush-bpi-price, .ush-bpi-compare-price, .alshaya-bundle-atc-prices, [data-essential-upsell-element]')) return;
        if (shopCurrency === target && presentmentCurrency === shopCurrency) return;

        var result = convertPresentmentCents(presentmentCents, presentmentCurrency);
        if (!result) return;

        var number = formatNumber(result.displayCents, result.target);
        if (number == null || number === 'NaN') return;

        paintNode(el, number, currencyLabel(result.target));
        converted += 1;

        if (debugEnabled() && converted <= 3) {
          log('convert', {
            market: config.marketHandle,
            country: config.country,
            originalCurrency: presentmentCurrency,
            shopCurrency: shopCurrency,
            targetCurrency: result.target,
            exchangeRate: result.rate,
            presentmentCents: presentmentCents,
            shopBaseCents: result.shopCents,
            convertedCents: result.displayCents,
            apiStatus: ratesState.source,
            cacheStatus: ratesState.cacheHit ? 'hit' : 'miss'
          });
        }
      });

      converted += convertProductCards();
      converted += convertBundlePrices();
      converted += convertEssentialUpsellPrices();
      log('updated nodes', { count: converted, total: nodes.length, target: target, api: ratesState.source });
    } catch (error) {
      log('update failed', error && error.message);
    } finally {
      painting = false;
    }
  }

  function convertProductCards() {
    var cards = document.querySelectorAll('product-card[data-currency-card], product-card[data-currency-price]');
    var painted = 0;
    cards.forEach(function (card) {
      var raw = card.getAttribute('data-price');
      if (raw == null || raw === '') return;
      var presentmentCents = Number(raw);
      if (!Number.isFinite(presentmentCents)) return;
      var presentmentCurrency = card.getAttribute('data-presentment-currency') || config.presentmentCurrency;
      var result = convertPresentmentCents(presentmentCents, presentmentCurrency);
      if (!result) return;
      var number = formatNumber(result.displayCents, result.target);
      var label = currencyLabel(result.target);
      if (number == null || number === 'NaN') return;

      card.querySelectorAll('.price-digits').forEach(function (el) {
        el.textContent = number;
      });
      card.querySelectorAll('.price-currency').forEach(function (el) {
        applyCurrencyLabel(el, label);
      });

      var compareRaw = card.getAttribute('data-compare-price');
      if (compareRaw && Number(compareRaw) > presentmentCents) {
        var compareResult = convertPresentmentCents(Number(compareRaw), presentmentCurrency);
        if (compareResult) {
          var compareNumber = formatNumber(compareResult.displayCents, compareResult.target);
          if (compareNumber) {
            card.querySelectorAll('.compare-digits').forEach(function (el) {
              el.textContent = compareNumber;
            });
            card.querySelectorAll('.compare-currency').forEach(function (el) {
              applyCurrencyLabel(el, label);
            });
          }
        }
      }
      painted += 1;
    });
    if (painted) log('updated product cards', painted);
    return painted;
  }

  function bundlePriceText(el) {
    var money = el.classList.contains('money') ? el : el.querySelector('.money');
    return ((money || el).textContent || '').replace(/\s+/g, ' ').trim();
  }

  function parseBundleMajor(text) {
    var source = String(text || '');
    source = source.replace(/[٠١٢٣٤٥٦٧٨٩]/g, function (digit) {
      return String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit));
    });
    source = source.replace(/[۰۱۲۳۴۵۶۷۸۹]/g, function (digit) {
      return String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit));
    });
    source = source.replace(/٫/g, '.').replace(/٬/g, '').replace(/,/g, '');
    source = source.replace(
      /د\.ك\.?|K\.D\.?|KWD|BHD|SAR|AED|QAR|QR|OMR|EGP|JOD|IQD|GBP|£|\uE900|\u20C1|⃁|ر\.س\.?|ر\.ق\.?|د\.إ\.?|د\.ب\.?|ر\.ع\.?/gi,
      ''
    );
    source = source.replace(/[^\d.]/g, '');
    var dot = source.indexOf('.');
    if (dot !== -1) {
      source = source.slice(0, dot + 1) + source.slice(dot + 1).replace(/\./g, '');
    }
    var integerPart = source.split('.')[0] || '';
    if (integerPart.length > 6) return null;
    var major = parseFloat(source);
    if (!Number.isFinite(major) || major <= 0 || major > MAX_MAJOR) return null;
    return major;
  }

  function isAppBundlePrice(text) {
    var t = String(text || '').replace(/\s+/g, ' ').trim();
    return /^(KWD|BHD|SAR|AED|QAR|OMR|EGP|JOD|IQD|GBP)\s/.test(t) || /(?:د\.ك|K\.D\.?)\s*$/.test(t);
  }

  function showingConvertedBundle(el, current) {
    if (el.querySelector('.f-price__currency--sar, .sar-symbol')) return true;
    var painted = el.getAttribute('data-native-painted');
    if (!painted) return false;
    var paintedNumber = String(painted).split(' ')[0];
    return paintedNumber && current.indexOf(paintedNumber) !== -1;
  }

  function stampBundleNode(el, presentmentCurrency) {
    if (!el) return false;
    var existing = el.getAttribute('data-price');
    if (existing && Number(existing) > 1e9) {
      el.removeAttribute('data-price');
      existing = null;
    }
    var current = bundlePriceText(el);
    var appSource = isAppBundlePrice(current);

    if (showingConvertedBundle(el, current) && !appSource) return !!existing;

    var major = parseBundleMajor(current);
    if (major == null) return !!existing;
    var cents = Math.round(major * shopifySubunit(presentmentCurrency));
    if (!Number.isFinite(cents) || cents < 0 || cents > 1e9) return !!existing;
    if (existing && String(cents) === String(existing) && showingConvertedBundle(el, current)) return true;
    el.setAttribute('data-currency-price', '');
    el.setAttribute('data-price', String(cents));
    el.setAttribute('data-presentment-currency', presentmentCurrency);
    el.removeAttribute('data-native-painted');
    return true;
  }

  function clearBundleStamps(root) {
    root = root || document;
    root.querySelectorAll('.ush-bpi-price, .ush-bpi-compare-price, .alshaya-bundle-atc-sale, .alshaya-bundle-atc-compare').forEach(function (el) {
      el.removeAttribute('data-price');
      el.removeAttribute('data-native-painted');
      el.removeAttribute('data-currency-price');
    });
    var wrap = document.querySelector('.alshaya-bundle-atc-prices');
    if (wrap) wrap.removeAttribute('data-alshaya-atc');
  }

  function paintBundleNode(el, number, label) {
    var html = labeledHtml(number, label);
    var painted = isSarLabel(label) ? number + ' SAR' : number + ' ' + label;
    if (el.getAttribute('data-native-painted') === painted) return;
    var money = el.classList.contains('money') ? el : el.querySelector('.money');
    if (money) {
      money.innerHTML = html;
      el.querySelectorAll('.alshaya-currency').forEach(function (mark) {
        mark.remove();
      });
    } else {
      el.innerHTML = html;
    }
    el.setAttribute('data-native-painted', painted);
  }

  function convertBundlePrices() {
    var target = targetCurrency();
    var presentmentCurrency = config.presentmentCurrency || config.shopCurrency;
    var needsConvert = !(config.shopCurrency === target && config.presentmentCurrency === config.shopCurrency);
    var selectors = [
      '.ush-bpi-price',
      '.ush-bpi-compare-price',
      '.alshaya-bundle-atc-sale',
      '.alshaya-bundle-atc-compare'
    ].join(',');
    var nodes = document.querySelectorAll(selectors);
    var painted = 0;
    nodes.forEach(function (el) {
      if (!stampBundleNode(el, presentmentCurrency)) return;
      var presentmentCents = Number(el.getAttribute('data-price'));
      if (!Number.isFinite(presentmentCents) || presentmentCents <= 0 || presentmentCents > 1e9) return;
      var number;
      var label;
      if (needsConvert) {
        var result = convertPresentmentCents(presentmentCents, el.getAttribute('data-presentment-currency') || presentmentCurrency);
        if (!result) return;
        number = formatNumber(result.displayCents, result.target);
        label = currencyLabel(result.target);
      } else {
        number = formatNumber(presentmentCents, presentmentCurrency);
        label = currencyLabel(presentmentCurrency);
      }
      if (number == null || number === 'NaN' || !label) return;
      paintBundleNode(el, number, label);
      painted += 1;
    });
    if (painted) log('updated bundle prices', painted);
    return painted;
  }

  function essentialPresentment(el) {
    var host = el.closest('[presentment-currency], essential-upsell-cross-sell-app-block');
    var code = host && host.getAttribute('presentment-currency');
    return code || config.presentmentCurrency || config.shopCurrency;
  }

  function essentialMajor(el) {
    var raw =
      el.getAttribute('data-regular-price-value') ||
      el.getAttribute('data-sale-price-value') ||
      el.getAttribute('data-compare-at-price-value') ||
      el.getAttribute('data-price-value');
    if (raw != null && raw !== '') {
      var fromAttr = parseFloat(raw);
      if (Number.isFinite(fromAttr) && fromAttr > 0 && fromAttr <= MAX_MAJOR) return fromAttr;
    }
    return parseBundleMajor(el.textContent);
  }

  function essentialAlreadyPainted(el, painted) {
    if (el.querySelector('.f-price__currency--sar, .sar-symbol')) {
      return el.getAttribute('data-native-painted') === painted;
    }
    var current = (el.textContent || '').replace(/\s+/g, ' ').trim();
    return painted && current === painted;
  }

  function convertEssentialUpsellPrices() {
    var nodes = document.querySelectorAll(
      '[data-essential-upsell-element="regular-price"], [data-essential-upsell-element="sale-price"], [data-essential-upsell-element="compare-at-price"], [data-essential-upsell-element="compare-price"]'
    );
    if (!nodes.length) return 0;
    var painted = 0;
    nodes.forEach(function (el) {
      var presentmentCurrency = essentialPresentment(el);
      var major = essentialMajor(el);
      if (major == null || !Number.isFinite(major) || major <= 0) return;
      var cents = Math.round(major * shopifySubunit(presentmentCurrency));
      if (!Number.isFinite(cents) || cents <= 0 || cents > 1e9) return;

      var result = convertPresentmentCents(cents, presentmentCurrency);
      var number;
      var label;
      if (result) {
        number = formatNumber(result.displayCents, result.target);
        label = currencyLabel(result.target);
      } else {
        number = formatNumber(cents, presentmentCurrency);
        label = currencyLabel(presentmentCurrency);
      }
      if (number == null || number === 'NaN' || !label) return;
      var paintedKey = isSarLabel(label) ? number + ' SAR' : number + ' ' + label;
      if (essentialAlreadyPainted(el, paintedKey)) return;
      paintBundleNode(el, number, label);
      painted += 1;
    });
    if (painted) log('updated essential upsell prices', painted);
    return painted;
  }

  function observeEssentialUpsell() {
    if (typeof MutationObserver !== 'function') return;
    document
      .querySelectorAll('essential-upsell-cross-sell-app-block, [data-essential-upsell-element="container"]')
      .forEach(function (container) {
        if (container.getAttribute('data-native-currency-observed-eu') === 'true') return;
        container.setAttribute('data-native-currency-observed-eu', 'true');
        var observer = new MutationObserver(function () {
          scheduleUpdate();
        });
        observer.observe(container, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['data-regular-price-value', 'data-sale-price-value', 'data-compare-at-price-value', 'data-price-value']
        });
      });
  }

  function scheduleUpdate() {
    if (updateTimer) clearTimeout(updateTimer);
    updateTimer = setTimeout(function () {
      updateTimer = 0;
      try {
        updateAll();
      } catch (error) {
        log('update failed', error && error.message);
      }
    }, 60);
  }

  function onVariantChange(event) {
    var variant = event.detail && event.detail.variant;
    if (variant && typeof variant.price !== 'undefined') {
      document.querySelectorAll('[data-deema-amount][data-currency-price]').forEach(function (el) {
        el.setAttribute('data-price', String(Math.round(Number(variant.price) / 4)));
      });
    }
    scheduleUpdate();
  }

  function onCartChange(event) {
    var cart = event.detail && event.detail.cart;
    if (cart && typeof cart.total_price !== 'undefined') {
      document.querySelectorAll('[data-deema-amount][data-currency-price]').forEach(function (el) {
        el.setAttribute('data-price', String(Math.round(Number(cart.total_price) / 4)));
      });
    }
    initFreeShippingBars();
    scheduleUpdate();
  }

  function observeScopedContainers() {
    if (typeof MutationObserver !== 'function') return;
    SCOPED_CONTAINERS.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (container) {
        if (container.getAttribute('data-native-currency-observed') === 'true') return;
        container.setAttribute('data-native-currency-observed', 'true');
        var observer = new MutationObserver(function () {
          scheduleUpdate();
        });
        observer.observe(container, { childList: true, subtree: true, characterData: true });
      });
    });
    observeEssentialUpsell();
  }

  function bindEvents() {
    document.addEventListener('product:rerender', scheduleUpdate, true);
    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      if (target.closest('[data-essential-upsell-element="variant-select"], [data-essential-upsell-element="product"]')) {
        setTimeout(scheduleUpdate, 50);
      }
    }, true);
    document.addEventListener('variant:change', onVariantChange, true);
    document.addEventListener('cart:change', onCartChange, true);
    document.addEventListener('facet:update', function () {
      setTimeout(function () {
        observeScopedContainers();
        scheduleUpdate();
      }, 300);
    });
    document.addEventListener('shopify:section:load', function () {
      observeScopedContainers();
      observeShippingBars();
      initFreeShippingBars();
      scheduleUpdate();
    });
    window.addEventListener('load', function () {
      observeScopedContainers();
      scheduleUpdate();
      setTimeout(scheduleUpdate, 400);
      setTimeout(function () {
        observeScopedContainers();
        scheduleUpdate();
      }, 1200);
      setTimeout(function () {
        observeScopedContainers();
        scheduleUpdate();
      }, 2200);
    });
    observeScopedContainers();
  }

  function start() {
    if (started) return;
    started = true;
    config = parseConfig();
    if (!config || !config.shopCurrency) {
      log('missing config, abort');
      return;
    }

    window.NATIVE_CURRENCY_DEBUG = debugEnabled();
    var target = targetCurrency();
    log('init', {
      market: config.marketHandle,
      country: config.country,
      shopCurrency: config.shopCurrency,
      presentmentCurrency: config.presentmentCurrency,
      targetCurrency: target,
      shopSubunit: config.shopSubunit,
      presentmentSubunit: config.presentmentSubunit,
      shopifyRate: shopifyPresentmentRate()
    });

    bindEvents();
    observeShippingBars();
    initFreeShippingBars();
    scheduleUpdate();

    if (config.shopCurrency === target && config.presentmentCurrency === config.shopCurrency) {
      log('no conversion needed, still painting essential upsell labels');
      return;
    }

    if (config.shopCurrency === target) {
      log('display shop currency from presentment via Shopify.currency.rate');
      scheduleUpdate();
      return;
    }

    CurrencyService.getRates(config.shopCurrency).then(function (rates) {
      if (!rates && !staticRate(config.shopCurrency, target) && config.shopCurrency !== target) {
        log('no rates available, keeping liquid prices');
        return;
      }
      scheduleUpdate();
    });
  }

  function shippingBarRemainingHtml(cents, currency) {
    var locale = isArabic() ? 'ar' : 'en';
    var decimals = DECIMAL_PLACES[currency] || DEFAULT_DECIMALS;
    var number = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(Math.max(0, Number(cents) || 0) / 100);
    if (currency === 'SAR') {
      return number + ' ' + sarMarkHtml();
    }
    return number + ' ' + currencyLabel(currency);
  }

  function bindFreeShippingBar(el) {
    if (!el || el.getAttribute('data-alshaya-shipping') === 'true') return;
    var currency = el.getAttribute('data-currency');
    var fixed = el.getAttribute('data-fixed-threshold');
    if (!fixed) return;
    el.setAttribute('data-alshaya-shipping', 'true');

    var factor = parseFloat(el.getAttribute('data-total-factor'));
    if (!Number.isFinite(factor) || factor <= 0) factor = 1;

    function applyThreshold() {
      el.threshold = fixed;
      if (typeof el._updateMessage === 'function') el._updateMessage();
    }

    if (currency && currency !== 'KWD') {
      el._updateMessage = function () {
        var messageElement = this.querySelector('span');
        if (!messageElement) return;
        if (this.totalPrice >= this.threshold) {
          messageElement.innerHTML = this.getAttribute('reached-message');
          return;
        }
        var remaining = Math.max(0, this.threshold - this.totalPrice);
        var replacement =
          '<span class="bold text-accent">' + shippingBarRemainingHtml(remaining, currency) + '</span>';
        messageElement.innerHTML = this.getAttribute('unreached-message').replace(
          new RegExp('({{.*}})', 'g'),
          replacement
        );
      };
    }

    document.addEventListener('cart:change', function (event) {
      if (!el.isConnected) return;
      el.threshold = fixed;
      if (factor === 1 || !event.detail || !event.detail.cart) return;
      var cart = event.detail.cart;
      var priceForItems = (cart.items || [])
        .filter(function (item) {
          return item.requires_shipping;
        })
        .reduce(function (sum, item) {
          return sum + item.final_line_price;
        }, 0);
      var cartDiscount = (cart.cart_level_discount_applications || []).reduce(function (sum, discount) {
        return sum + discount.total_allocated_amount;
      }, 0);
      el.totalPrice = (priceForItems - cartDiscount) * factor;
    });

    applyThreshold();
    setTimeout(applyThreshold, 0);
    setTimeout(applyThreshold, 50);
  }

  function initFreeShippingBars() {
    document.querySelectorAll('free-shipping-bar[data-fixed-threshold]').forEach(bindFreeShippingBar);
  }

  function observeShippingBars() {
    document.querySelectorAll('#cart-drawer, .cart-header').forEach(function (root) {
      if (root.getAttribute('data-alshaya-shipping-observed') === 'true') return;
      root.setAttribute('data-alshaya-shipping-observed', 'true');
      if (typeof MutationObserver !== 'function') return;
      new MutationObserver(function () {
        initFreeShippingBars();
      }).observe(root, { childList: true, subtree: true });
    });
  }

  window.NativeCurrency = {
    CurrencyService: CurrencyService,
    refresh: scheduleUpdate,
    restampBundle: function (root) {
      clearBundleStamps(root);
      scheduleUpdate();
    },
    isPainting: function () {
      return painting;
    },
    config: function () {
      return config;
    }
  };

  function boot() {
    start();
    observeShippingBars();
    initFreeShippingBars();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
